import {
  isSubsetSchema,
  isSchemaAssignable,
  getUnionSchema,
  getIntersectionSchema,
  getConstantSchema,
  getBlankValueForSchema,
  getArrayItemSchema,
  getPossibleObjectSchemaProperties,
  traverseSchema,
  getSchemaAnnotation,
} from '@sapappgyver/appgyver-schemas';
import { cloneDeep, sortBy } from 'lodash';
import { UNKNOWN_SCHEMA } from './schemas';

export const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const defaultSchemas = [
  {
    type: 'string',
    examples: ['Lorem ipsum'],
    default: '',
  },
  {
    type: 'string',
    format: 'email',
    examples: ['john.smith@example.com'],
  },
  {
    type: 'string',
    format: 'date-time',
    examples: [new Date().toISOString()],
  },
  {
    type: 'string',
    format: 'date',
    examples: [new Date().toISOString().slice(0, 'YYYY-MM-DD'.length)],
  },
  {
    type: 'string',
    format: 'url',
    examples: ['https://www.example.com'],
  },
  {
    type: 'string',
    format: 'web-url',
    examples: ['https://www.example.com'],
  },
  {
    type: 'string',
    format: 'icon',
    examples: ['star'],
  },
  {
    type: 'string',
    format: 'color',
    examples: ['#000000'],
  },
  {
    type: 'string',
    format: 'uuid',
    examples: ['4d7dfe35-9cd3-4eba-8051-4b91858abb35'],
  },
  {
    type: 'string',
    format: 'integer',
    examples: ['123'],
    default: '0',
  },
  {
    type: 'string',
    format: 'number',
    examples: ['123.45'],
    default: '0',
  },
  {
    type: 'string',
    format: 'percentage',
    examples: ['100%'],
    default: '0%',
  },
  {
    type: 'string',
    format: 'mime-type',
    examples: ['text/plain'],
  },
  {
    type: 'string',
    format: 'local-filesystem-reference',
  },
  {
    type: 'number',
    examples: [123],
    default: 0,
  },
  {
    type: 'boolean',
    default: false,
  },
  {
    type: 'schema',
    schema: { type: 'json' },
  },
  {
    type: 'schematicValue',
    schema: {
      type: 'object',
      properties: {},
      additionalProperties: {
        anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }],
      },
    },
  },
  {
    type: 'schematicValue',
    schema: {
      type: 'array',
      items: {
        type: 'object',
        properties: {},
        additionalProperties: {
          anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }],
        },
      },
    },
  },
  {
    type: 'object',
    properties: { id: { type: 'string', examples: ['6237ea82-854e-4242-a8db-21632c23d143'] } },
  },
  {
    type: 'object',
    properties: {},
  },
  {
    type: 'array',
    items: { type: 'string', examples: ['Lorem ipsum'] },
    default: [],
  },
  {
    type: 'array',
    items: {
      type: 'object',
      properties: { id: { type: 'string', examples: ['6237ea82-854e-4242-a8db-21632c23d143'] } },
    },
    default: [],
  },
];

export function getVariableName(args) {
  const { variableArray, baseName, index } = args;
  // remove all empty spaces in the name
  const varName = `${baseName}${index}`
    .split(/\s+/)
    .map((e) => e.replace(/[^a-zA-Z0-9]+/g, ''))
    .map((e, i) => (i === 0 ? e : capitalizeFirstLetter(e)))
    .join('');

  if (!variableArray.some((item) => item.name === varName)) {
    return varName;
  }
  return getVariableName({
    index: index + 1,
    baseName,
    variableArray,
  });
}
export function createVariable({ variableType, variableGroups, newSchema, name }) {
  const baseName = variableType === 'parameters' ? 'parameter' : 'variable';

  const variableName = getVariableName({
    index: 1,
    baseName: name || baseName,
    variableArray: variableGroups,
  });

  if (newSchema) {
    return {
      name: variableName,
      schema: newSchema,
    };
  }

  const schema = cloneDeep(getDefaultSchema({ type: 'string' }, variableType !== 'parameters', true));
  if (variableType === 'app') {
    schema.defaultValueTooltip = 'App variables are initialized when the app is started.';
  } else if (variableType === 'page') {
    schema.defaultValueTooltip =
      'Page variables exist only in the context of the current page. They are reset to the Initial value every time a page is opened';
  }

  return {
    name: variableName,
    schema,
  };
}
export function createDataResourceObject({ dataVariables, name }) {
  const referenceName = getVariableName({
    index: 1,
    baseName: name,
    variableArray: dataVariables,
  });
  const resourceObject = {
    resource_name: name,
    reference_name: referenceName,
    action_name: 'data.findAll',
    query: {},
  };
  return resourceObject;
}
export function getDefaultSchema(metaSchema, enableDefault, enableExamples) {
  if (!enableDefault) {
    const { default: _default, ...schema } = getDefaultSchema(metaSchema, true, enableExamples);
    return schema;
  }
  let defaultSchema = defaultSchemas.find((schema) => isSubsetSchema(schema, metaSchema, true));
  if (defaultSchema) {
    // TODO: This deep-cloning is not necessary when schemas are immutable
    defaultSchema = JSON.parse(JSON.stringify(defaultSchema));
    const { examples, ...schema } = defaultSchema;
    if (!enableExamples) {
      // Ignore examples from the result
      return schema;
    }
    if (examples && !isSchemaAssignable({ ...schema, enum: examples }, metaSchema)) {
      // Some of the examples is not valid. Return without examples
      return schema;
    }
    return defaultSchema;
  }
  // What, what kind of meta schema is it??
  return { type: 'string' };
}

export function getSchemaValueOptions(schema, emptyLabel) {
  if (!schema) {
    return { allowOtherValues: true, options: [] };
  }
  if (schema.enum && schema.enum.length) {
    const options = schema.enum.map((value) => ({
      value,
      label: emptyLabel != null && (value === '' || value == null) ? String(emptyLabel) : String(value),
      disabled: false,
    }));
    return { allowOtherValues: false, options, remainingSchema: null };
  }
  if (schema.anyOf && schema.anyOf.length) {
    let allowOtherValues = false;
    let remainingSchema = null;
    const options = [];
    schema.anyOf.forEach((subSchema) => {
      const subOptions = getSchemaValueOptions(subSchema, emptyLabel);
      if (subOptions.allowOtherValues) {
        allowOtherValues = true;
        const otherSchema = subOptions.remainingSchema;
        if (otherSchema != null) {
          remainingSchema = remainingSchema != null ? getUnionSchema([remainingSchema, otherSchema]) : otherSchema;
        }
      }
      const { title } = subSchema;
      if (subOptions.options.length === 1 && title) {
        const { value } = subOptions.options[0];
        if (!options.some((option) => option.value === value)) {
          options.push({ value, label: title, disabled: false });
        }
      } else {
        options.push(...subOptions.options.filter((option) => !options.some(({ value }) => value === option.value)));
      }
    });
    return { allowOtherValues, options, remainingSchema };
  }
  if (schema.allOf && schema.allOf.length) {
    const subResults = schema.allOf.map((subSchema) => getSchemaValueOptions(subSchema, emptyLabel));
    return subResults.reduce((prevResult, nextResult) => {
      // Combine all known options
      const allOptions = prevResult.options.slice();
      nextResult.options.forEach((option) => {
        if (!allOptions.some(({ value }) => value === option.value)) {
          allOptions.push(option);
        }
      });
      const allowOtherValues = prevResult.allowOtherValues && nextResult.allowOtherValues;
      const remainingSchema = getIntersectionSchema([prevResult.remainingSchema, nextResult.remainingSchema]);
      return {
        allowOtherValues,
        remainingSchema,
        options: allOptions.map((option) => ({
          ...option,
          disabled: allowOtherValues
            ? option.disabled
            : !(
                prevResult.options.some(({ value, disabled }) => value === option.value && !disabled) &&
                nextResult.options.some(({ value, disabled }) => value === option.value && !disabled)
              ),
        })),
      };
    });
  }
  // Allow anything
  return { allowOtherValues: true, options: [], remainingSchema: schema };
}

/**
 * Returns a "default" value for the given schema. It uses the `default` annotation
 * if present, and otherwise falls back to the same behavior than `getBlankValueForSchema`.
 *
 * @param {*} schema schema to which the value must match
 */
export function getDefaultValueForSchema(schema) {
  // If the schema has a default value, then use it as a blank value
  if (
    schema &&
    typeof schema.default !== 'undefined' &&
    isSchemaAssignable(getConstantSchema(schema.default), schema)
  ) {
    return schema.default;
  }
  return getBlankValueForSchema(schema);
}

const singularDisplayNamesByFormat = {
  'email': 'email address',
  'date-time': 'date/time text',
  'date': 'date text',
  'url': 'URL',
  'uri': 'URI',
  'web-url': 'web URL',
  'icon': 'icon name',
  'color': 'color code',
  'uuid': 'UUID',
  'integer': 'integer text',
  'number': 'numeric text',
  'percentage': 'percentage text',
  'mime-type': 'MIME content type',
  'local-filesystem-reference': 'local filesystem path',
  '*': 'any text',
};
const pluralDisplayNamesByFormat = {
  'email': 'email addresses',
  'date-time': 'date/time texts',
  'date': 'date texts',
  'url': 'URLs',
  'web-url': 'web URLs',
  'icon': 'icon names',
  'color': 'color codes',
  'uuid': 'UUIDs',
  'integer': 'integer texts',
  'number': 'numeric texts',
  'percentage': 'percentage texts',
  'mime-type': 'MIME content types',
  'local-filesystem-reference': 'local filesystem paths',
  '*': 'any texts',
};
const singularDisplayNamesByType = {
  number: 'number',
  boolean: 'true/false',
  schematicValue: 'schematic value',
  pageVariableRef: 'page variable',
  appVariableRef: 'app variable',
  resourceName: 'data entity',
  dataReferenceName: 'data variable',
  componentDataReferenceName: 'component data variable',
  cloudFunctionRef: 'Cloud function',
  spaProcessDefinitionRef: 'Process',
  buildActionRef: 'Build Action',
  btpDestinationRef: 'BTP Destination',
  httpRequestBodyRef: 'HTTP request body',
  httpRequestMethodRef: 'HTTP request method',
  componentId: 'component ID',
  page: 'page ID',
  event: 'logic event ID',
  service: 'service ID',
  json: 'any value',
  value: 'any value/reference',
  appVariableAssignment: 'app variable assignment',
  pageVariableAssignment: 'page variable assignment',
  dataReferenceAssignment: 'data variable assignment',
  componentDataReferenceAssignment: 'component data variable assignment',
  componentPropertyAssignment: 'component property assignment',
  componentProperty: 'component property reference',
  pageNavigation: 'page navigation',
  fontFamily: 'font family name',
  supportedLanguage: 'supported language',
  dataResourceRetrieveRequest: 'data entity retrieve request',
  dataResourceListRequest: 'data entity list request',
  dataResourceCreateRequest: 'data entity create request',
  dataResourceReplaceRequest: 'data entity replace request',
  dataResourceUpdateRequest: 'data entity update request',
  dataResourceDeleteRequest: 'data entity delete request',
  dataResourceRetrieveResponse: 'data entity retrieve response',
  dataResourceListResponse: 'data entity list response',
  dataResourceCreateResponse: 'data entity create response',
  dataResourceReplaceResponse: 'data entity replace response',
  dataResourceUpdateResponse: 'data entity update response',
  dataResourceDeleteResponse: 'data entity delete response',
  // THESE ARE DEPRECATED AND WILL BE REMOVED!
  resourceInstance: 'record instance',
  resourceCollectionInstance: 'record collection',
  dataResourceRecord: 'data entity record',
  dataReferenceRecord: 'data variable record',
  dataReferenceCollection: 'data variable collection',
};
const pluralDisplayNamesByType = {
  number: 'numbers',
  boolean: 'true/false values',
  schematicValue: 'schematic values',
  pageVariableRef: 'page variables',
  appVariableRef: 'app variables',
  resourceName: 'data entities',
  dataReferenceName: 'data variables',
  componentDataReferenceName: 'component data variables',
  cloudFunctionRef: 'Cloud functions',
  spaProcessDefinitionRef: 'Processes',
  buildActionRef: 'Build Actions',
  btpDestinationRef: 'BTP Destinations',
  httpRequestBodyRef: 'HTTP request bodies',
  httpRequestMethodRef: 'HTTP request methods',
  componentId: 'component IDs',
  page: 'page IDs',
  event: 'logic event IDs',
  service: 'service IDs',
  json: 'any values',
  value: 'any values/references',
  appVariableAssignment: 'app variable assignments',
  pageVariableAssignment: 'page variable assignments',
  dataReferenceAssignment: 'data variable assignments',
  componentDataReferenceAssignment: 'component data variable assignments',
  componentPropertyAssignment: 'component property assignments',
  componentProperty: 'component property references',
  pageNavigation: 'page navigations',
  fontFamily: 'font family names',
  supportedLanguage: 'supported languages',
  dataResourceRetrieveRequest: 'data entity retrieve requests',
  dataResourceListRequest: 'data entity list requests',
  dataResourceCreateRequest: 'data entity create requests',
  dataResourceReplaceRequest: 'data entity replace requests',
  dataResourceUpdateRequest: 'data entity update requests',
  dataResourceDeleteRequest: 'data entity delete requests',
  dataResourceRetrieveResponse: 'data entity retrieve responses',
  dataResourceListResponse: 'data entity list responses',
  dataResourceCreateResponse: 'data entity create responses',
  dataResourceReplaceResponse: 'data entity replace responses',
  dataResourceUpdateResponse: 'data entity update responses',
  dataResourceDeleteResponse: 'data entity delete responses',
  // THESE ARE DEPRECATED AND WILL BE REMOVED!
  resourceInstance: 'record instances',
  resourceCollectionInstance: 'record collections',
  dataResourceRecord: 'data entity records',
  dataReferenceRecord: 'data variable records',
  dataReferenceCollection: 'data variable collections',
};

export function getShortSchemaDisplayText(schema, plural = false) {
  let combinationString;
  if (schema) {
    const { referredValue, ...plainSchema } = schema;
    if (referredValue) {
      const baseText = getShortSchemaDisplayText(plainSchema, plural);
      const valueText = getShortSchemaDisplayText(referredValue, plural);
      return `${baseText} for ${valueText}`;
    }
    const { anyOf, allOf } = schema;
    if (anyOf) {
      combinationString = !anyOf.length
        ? 'nothing'
        : anyOf.map((subSchema) => getShortSchemaDisplayText(subSchema, plural)).join(' / ');
    } else if (allOf) {
      combinationString = !allOf.length
        ? 'nothing'
        : allOf.map((subSchema) => getShortSchemaDisplayText(subSchema, plural)).join(' & ');
    }
  }
  if (combinationString != null) {
    if (combinationString.length <= 40) {
      return combinationString;
    }
    return plural ? 'mixed types' : 'mixed type';
  }
  if (!schema || schema.type == null) {
    return plural ? 'unknown types' : 'unknown type';
  }
  const { type, subType } = schema;
  if (type === 'string') {
    const { format } = schema;
    if (!format) {
      return plural ? 'texts' : 'text';
    }
    if (format === 'web-url' && schema.uiType === 'image-picker') {
      return plural ? 'image URLs' : 'image URL';
    }
    const displayNamesByFormat = plural ? pluralDisplayNamesByFormat : singularDisplayNamesByFormat;
    const displayName = displayNamesByFormat[format];
    if (displayName) {
      return displayName;
    }
    // Unknown format
    return `${schema.format} ${plural ? 'texts' : 'text'}`;
  }
  if (type === 'array') {
    if (subType === 'navigation') {
      return 'navigation';
    }
    const itemSchema = getArrayItemSchema(schema);
    const baseName = plural ? 'lists' : 'list';
    if (!itemSchema) {
      return baseName;
    }
    return `${baseName} of ${getShortSchemaDisplayText(itemSchema, true)}`;
  }
  if (type === 'object') {
    const propNames = getPossibleObjectSchemaProperties(schema);
    const baseName = plural ? 'objects' : 'object';
    if (!propNames || !propNames.length) {
      return baseName;
    }
    return `${baseName} with ${propNames.length} ${propNames.length > 1 ? 'properties' : 'property'}`;
  }
  const displayNamesByType = plural ? pluralDisplayNamesByType : singularDisplayNamesByType;
  const displayName = displayNamesByType[type];
  return displayName || type;
}

/**
 * Returns whether or not the schema represents an object or unknown value so
 * that it would allow arbitrary properties. Also returns true if the schema
 * represents an array whose direct children are arbitrary. Note that otherwise
 * the check is NOT recursive, so one of the known properties could still have arbitrary schema.
 * @param {object} schema schema definition objects
 */
export function hasArbitrarySchema(schema) {
  if (!schema) {
    return true;
  }
  if (schema.anyOf) {
    return !schema.anyOf.length || schema.anyOf.some(hasArbitrarySchema);
  }
  if (schema.allOf) {
    return schema.allOf.every(hasArbitrarySchema);
  }
  const { type } = schema;
  if (!type || type === 'value' || type === 'json') {
    return true;
  }
  if (type === 'array') {
    return hasArbitrarySchema(schema.items);
  }
  if (type !== 'object') {
    return false;
  }
  return !schema.properties || !!schema.additionalProperties;
}

/**
 * Returns whether or not the given source schema is compatible with
 * the given target schema. This is determined whether the schema type is assignable
 * to another, but the comparison direction depends on the given binding direction type.
 *
 * @param {object|null} sourceSchema source (e.g. variable) schema
 * @param {object|null} targetSchema target (e.g. component property) schema
 * @param {string|null} [bindingType] type of the binding: "inbound"/"outbound"/"two-way"
 * @param {boolean} [strict] whether to use strict type checking (defaults to false)
 */
export function isBindingSchemaCompatible(sourceSchema, targetSchema, bindingType, strict = false) {
  const schema = sourceSchema || UNKNOWN_SCHEMA;
  if (bindingType === 'outbound') {
    // The target must be assignable to the source
    return isSchemaAssignable(targetSchema, schema, !strict);
  }
  // Otherwise the source must be assignable to the target
  return isSchemaAssignable(schema, targetSchema, !strict);
}

export function sortSchemaProperties(schema) {
  return traverseSchema(schema, (subSchema) => {
    if (!schema || schema.type !== 'object') {
      return subSchema;
    }
    const { properties, additionalProperties } = schema;
    let newSchema = schema;
    if (properties) {
      const newProperties = {};
      const sortedPropNames = sortBy(Object.keys(properties), (propName) => {
        const propSchema = properties[propName];
        const title = getSchemaAnnotation(propSchema);
        return (title ?? propName).toLowerCase();
      });
      sortedPropNames.forEach((propName) => {
        newProperties[propName] = sortSchemaProperties(properties[propName]);
      });
      newSchema = {
        ...newSchema,
        properties: newProperties,
      };
    }
    if (additionalProperties) {
      newSchema = {
        ...newSchema,
        additionalProperties: sortSchemaProperties(additionalProperties),
      };
    }
    return newSchema;
  });
}
